// Cover palette + Auto-title rule — ported verbatim from the render engine
// (src/render/cover.ts §14.8 / §14.5) so a color cover and its "auto" title
// colour match the printed cover. Keep in sync if the engine's palette changes.

export const COVER_PALETTE: Record<string, string> = {
  white: "#FFFFFF",
  sage: "#BDC9B3",
  dustyblue: "#A9BCCB",
  lavender: "#9E93B3",
  mauve: "#C3A2BF",
  ink: "#111111",
  olive: "#60644B",
  steelblue: "#576A8A",
  taupe: "#635856",
  plum: "#633F49",
};

// Auto title sentinels (distinct from the selectable-10).
export const AUTO_LIGHT_HEX = "#F4EFE7"; // "bone" — on dark backgrounds
export const AUTO_DARK_HEX = "#1A1A1D"; // "ink" sentinel — on light backgrounds

/** Palette id OR canonical #RRGGBB -> hex. */
export function coverColorHex(id: string): string {
  return COVER_PALETTE[id] ?? id;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}
function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function relLum({ r, g, b }: Rgb): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function contrastVs(a: Rgb, b: Rgb): number {
  const la = relLum(a);
  const lb = relLum(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** §14.5 Auto: pick whichever sentinel has higher WCAG contrast on the fill. */
export function autoTitleHex(fillHex: string): string {
  const fill = parseHex(fillHex);
  if (!fill) return AUTO_DARK_HEX;
  const light = parseHex(AUTO_LIGHT_HEX)!;
  const dark = parseHex(AUTO_DARK_HEX)!;
  return contrastVs(fill, light) >= contrastVs(fill, dark)
    ? AUTO_LIGHT_HEX
    : AUTO_DARK_HEX;
}
